import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Plus, Receipt, X as XIcon } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { AddExpenseSheet } from '@/components/construction/AddExpenseSheet';
import { AuthImg } from '@/components/construction/AuthImg';

interface Expense {
  id: string;
  expenseDate: string;
  category: string;
  amount: number | string;
  description: string | null;
  vendor: string | null;
  receiptUrl: string | null;
  createdAt: string;
}

const fmtSAR = (n: number) => `SAR ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s: string) => new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

export default function ExpensesListPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [lightbox, setLightbox] = useState<Expense | null>(null);

  const refresh = useCallback(async () => {
    if (!projectId) return;
    try {
      const r = await apiClient.get<Expense[]>(`/api/v1/construction/projects/${projectId}/expenses`);
      setExpenses(r.data);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { refresh(); }, [refresh]);

  const total = expenses.reduce((sum, e) => sum + Number(e.amount), 0);

  const categoryColor = (c: string) => {
    if (c === 'Tools') return 'bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-400';
    if (c === 'Consumables') return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400';
    return 'bg-muted text-muted-foreground';
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to={`/dashboard/construction`}>
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
          </Link>
          <div className="text-sm font-medium">Site expenses</div>
          <Button size="sm" className="gap-2" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4" /> Log
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-4 space-y-3">
        <div className="border border-border rounded-lg p-4">
          <div className="text-xs text-muted-foreground uppercase tracking-wider">Total expenses</div>
          <div className="text-2xl font-display mt-1">{fmtSAR(total)}</div>
          <div className="text-xs text-muted-foreground mt-1">{expenses.length} entr{expenses.length === 1 ? 'y' : 'ies'}</div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && !loading && (
          <div className="rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm p-3">{error}</div>
        )}

        {!loading && !error && expenses.length === 0 && (
          <div className="text-center py-12 text-muted-foreground text-sm">
            <Receipt className="h-8 w-8 mx-auto mb-2 opacity-40" />
            No expenses logged yet. Tap Log to add the first one.
          </div>
        )}

        <div className="space-y-2">
          {expenses.map((e) => (
            <div key={e.id} className="border border-border rounded-lg p-3 flex items-center gap-3">
              {e.receiptUrl ? (
                <button
                  type="button"
                  onClick={() => setLightbox(e)}
                  className="flex-shrink-0 h-14 w-14 rounded overflow-hidden bg-muted"
                  aria-label="View receipt"
                >
                  <AuthImg src={e.receiptUrl} alt="" className="h-14 w-14 object-cover" />
                </button>
              ) : (
                <div className="flex-shrink-0 h-14 w-14 rounded bg-muted flex items-center justify-center">
                  <Receipt className="h-5 w-5 text-muted-foreground" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${categoryColor(e.category)}`}>{e.category}</span>
                  <span className="font-medium text-sm">{fmtSAR(Number(e.amount))}</span>
                </div>
                {e.description && <div className="text-sm truncate mt-0.5">{e.description}</div>}
                <div className="text-xs text-muted-foreground mt-0.5">
                  {fmtDate(e.expenseDate)}{e.vendor ? ` · ${e.vendor}` : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>

      {projectId && (
        <AddExpenseSheet open={addOpen} onClose={() => setAddOpen(false)} onCreated={refresh} projectId={projectId} />
      )}

      {/* Receipt lightbox */}
      {lightbox && lightbox.receiptUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            aria-label="Close"
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 h-10 w-10 inline-flex items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
          >
            <XIcon className="h-5 w-5" />
          </button>
          <div onClick={(e) => e.stopPropagation()} className="max-w-full max-h-full">
            <AuthImg src={lightbox.receiptUrl} alt={lightbox.description ?? 'Receipt'} className="max-w-full max-h-[85vh] object-contain rounded" loading="eager" />
            <div className="mt-3 text-white text-sm text-center">
              {fmtDate(lightbox.expenseDate)} · {lightbox.category} · {fmtSAR(Number(lightbox.amount))}
              {lightbox.description && <div className="text-xs opacity-80 mt-1">{lightbox.description}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
